// frontend/src/components/detail/tables/SituationsForProductTables.jsx
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, Typography, Tooltip, Link as MuiLink
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { slugify } from '../../../utils/slug';

/* ---------- helpers ---------- */

function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const parts = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        return `${k}: ${v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' • ') : '—';
}

function buildRows(customerStories = [], arrayKey /* "current_solution_still_used_in_these_situations" | "not_used_in_these_situations" */) {
  const map = new Map(); // catSlug -> { category, occurrences, instances: [], href }
  for (const s of customerStories || []) {
    const arr = Array.isArray(s?.[arrayKey]) ? s[arrayKey] : [];
    for (const it of arr) {
      const cat = it?.situation_category || 'Uncategorized';
      const slug = slugify(cat);
      if (!map.has(slug)) {
        map.set(slug, {
          category: cat,
          occurrences: 0,
          href: `/situation/${slug}`, // details page you’ll build later
          instances: [],
        });
      }
      const row = map.get(slug);
      row.occurrences += 1;
      row.instances.push({
        name: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        demographics: formatDemographics(s.segmentation_data),
        situation: it?.situation || '—',
        quote: it?.quote || '',
        // dummy deep-link for quote (timestamp optional in your JSON)
        quoteHref: '#',
        interviewHref: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      });
    }
  }

  const rows = [...map.values()];
  rows.sort((a, b) => b.occurrences - a.occurrences || a.category.localeCompare(b.category));
  // newest-first inside each instance set
  rows.forEach(r => r.instances.sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0)));
  return rows;
}

/* ---------- generic table ---------- */

function SituationsTable({ title, rows }) {
  const [open, setOpen] = React.useState({});
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  if (!rows.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No situations found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Situation</TableCell>
              <TableCell align="right">Occurrences</TableCell>
              <TableCell align="center" width={56}>Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const key = slugify(r.category || 'row');
              return (
                <React.Fragment key={key}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => toggle(key)} aria-label="Expand row">
                        {open[key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.category || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{r.occurrences}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Open situation details">
                        <span>
                          <IconButton size="small" component="a" href={r.href} aria-label="Open situation details">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* expanded: interviews */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                      <Collapse in={!!open[key]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, pl: 6 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Interviews with this situation
                          </Typography>
                          {r.instances.length ? (
                            <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Demographics</TableCell>
                                  <TableCell>Situation (raw)</TableCell>
                                  <TableCell>Quote</TableCell>
                                  <TableCell align="center" width={56}>Open</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {r.instances.map((inst, i) => (
                                  <TableRow key={`${key}-inst-${i}`} hover>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {inst.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {inst.date || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {inst.demographics || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{inst.situation || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      {inst.quote ? (
                                        <MuiLink href={inst.quoteHref || '#'} underline="hover">
                                          <PlayCircleOutlineIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                                          <Typography component="span" variant="body2">
                                            {inst.quote}
                                          </Typography>
                                        </MuiLink>
                                      ) : (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Open interview">
                                        <span>
                                          <IconButton
                                            size="small"
                                            component="a"
                                            href={inst.interviewHref}
                                            target="_blank"
                                            rel="noopener"
                                            aria-label="Open interview"
                                          >
                                            <OpenInNewIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              No interviews found for this situation.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/* ---------- exported wrapper that renders both tables ---------- */

export default function SituationsForProductTables({ customerStories = [] }) {
  // IMPORTANT: Do NOT filter by product here (the page already passes filtered interviews).
  const continueRows = React.useMemo(
    () => buildRows(customerStories, 'current_solution_still_used_in_these_situations'),
    [customerStories]
  );
  const stopRows = React.useMemo(
    () => buildRows(customerStories, 'not_used_in_these_situations'),
    [customerStories]
  );

  return (
    <Box sx={{ mt: 2 }}>
      <SituationsTable
        title="Situations where they continue using this solution"
        rows={continueRows}
      />
      <SituationsTable
        title="Situations where they no longer use this solution"
        rows={stopRows}
      />
    </Box>
  );
}
